// Estado de resultados mensual en pesos colombianos (traído del prototipo). Se arma con los pedidos que cuentan como
// venta y los gastos; los años anteriores al sistema salen del histórico de los archivos. Funciones puras, con pruebas.
import { cuenta, totalesPedido, type Pedido } from './pedidos';

export interface Mes { pedidos: number; prendas: number; brutas: number; descuentos: number; netas: number; costo: number; flete: number; transporte: number; operacion: number }
export interface MesDerivado extends Mes {
  utilBruta: number; logNeto: number; postLog: number; preop: number; mBruto: number; mPostLog: number; mPreop: number; ticket: number; prendasPed: number; precioPrenda: number;
}
export type Tipo = 'n' | 'n1' | '$' | '%';
export const FILAS_PYG: [keyof MesDerivado, string, Tipo, string?][] = [
  ['pedidos', '# Pedidos', 'n'], ['prendas', 'Prendas vendidas', 'n'], ['brutas', 'Ventas brutas', '$'], ['descuentos', 'Descuentos', '$'], ['netas', 'Ventas netas', '$', 'fuerte'],
  ['costo', 'Costo de ventas', '$'], ['utilBruta', 'Utilidad bruta', '$', 'fuerte'], ['flete', 'Envío cobrado al cliente', '$'], ['transporte', 'Pagado por despachos', '$'],
  ['logNeto', 'Gasto logístico neto', '$'], ['postLog', 'Utilidad post-logística', '$', 'fuerte'], ['operacion', 'Gastos de operación', '$'], ['preop', 'Utilidad preoperativa', '$', 'total'],
  ['mBruto', 'Margen bruto', '%'], ['mPostLog', 'Margen post-logístico', '%'], ['mPreop', 'Margen preoperativo', '%'],
  ['ticket', 'Ticket promedio por pedido', '$'], ['prendasPed', 'Prendas por pedido', 'n1'], ['precioPrenda', 'Precio promedio por prenda', '$'],
];

const vacio = (): Mes => ({ pedidos: 0, prendas: 0, brutas: 0, descuentos: 0, netas: 0, costo: 0, flete: 0, transporte: 0, operacion: 0 });
export function derivar(m: Mes): MesDerivado {
  const utilBruta = m.netas - m.costo, logNeto = m.transporte - m.flete, postLog = utilBruta - logNeto, preop = postLog - m.operacion;
  const r = (a: number, b: number) => (b ? a / b : 0);
  return { ...m, utilBruta, logNeto, postLog, preop, mBruto: r(utilBruta, m.netas), mPostLog: r(postLog, m.netas), mPreop: r(preop, m.netas),
    ticket: r(m.netas, m.pedidos), prendasPed: r(m.prendas, m.pedidos), precioPrenda: r(m.brutas, m.prendas) };
}
function anual(meses: Mes[]): Mes {
  const a = vacio();
  for (const m of meses) for (const k of Object.keys(a) as (keyof Mes)[]) a[k] += m[k];
  return a;
}

export interface GastoPyG { fecha: string; categoria_id: number; valor: number; moneda?: string; tasa_cop?: number }
// Valor del gasto en pesos. Los gastos en dólares se convierten con la tasa guardada en cada uno (la de ese día, como en los pedidos).
// Sin moneda (gastos de antes de existir la columna) se leen como pesos.
export const gastoCop = (g: Pick<GastoPyG, 'valor' | 'moneda' | 'tasa_cop'>) => (+g.valor || 0) * (g.moneda && g.moneda.trim() !== 'COP' ? +(g.tasa_cop ?? 0) || 0 : 1);
export interface PyG { meses: MesDerivado[]; anual: MesDerivado; historico: boolean }

// Todo en COP: los pedidos en USD o DOP se convierten con la tasa guardada en cada pedido, y los gastos en USD con la guardada en cada gasto. El costo de ventas es el
// costo congelado de cada prenda (ya en pesos).
// Envío: lo que cobra el cliente (flete) es ingreso; lo que paga el negocio a la transportadora (costo_envio de cada
// pedido, en COP) es gasto y cuenta en el mes del pedido, igual que su venta. Los gastos de una categoría de logística
// (otros transportes, sin pedido) se suman al mismo gasto. Gasto logístico neto = gasto − ingreso.
export function pyg(pedidos: Pedido[], gastos: GastoPyG[], logistica: Set<number>, anio: number): PyG {
  const meses = Array.from({ length: 12 }, vacio);
  for (const p of pedidos) {
    if (!cuenta(p) || !p.fecha || +p.fecha.slice(0, 4) !== anio) continue;
    const m = meses[+p.fecha.slice(5, 7) - 1], t = totalesPedido(p), k = t.tasa;
    m.pedidos++; m.prendas += t.prendas; m.brutas += t.bruto * k; m.descuentos += t.descuento * k; m.netas += t.neto * k; m.costo += t.costo; m.flete += t.flete * k; m.transporte += +(p.costo_envio ?? 0);
  }
  for (const g of gastos) {
    if (!g.fecha || +g.fecha.slice(0, 4) !== anio) continue;
    const m = meses[+g.fecha.slice(5, 7) - 1];
    if (logistica.has(g.categoria_id)) m.transporte += gastoCop(g); else m.operacion += gastoCop(g);
  }
  return { meses: meses.map(derivar), anual: derivar(anual(meses)), historico: false };
}

// Años anteriores al sistema, desde el histórico de los archivos (filas año, mes, concepto, valor). Sin detalle de envío
// cobrado: el gasto logístico del archivo es neto, y el apoyo comercial (publicidad, agencia) va en gastos de operación.
export interface FilaHistorica { anio: number; mes: number; concepto: string; valor: number }
export function pygHistorico(filas: FilaHistorica[], anio: number): PyG | null {
  const del = filas.filter(f => f.anio === anio);
  if (!del.length) return null;
  const v = (mes: number, c: string) => +(del.find(f => f.mes === mes && f.concepto === c)?.valor || 0);
  const meses: Mes[] = Array.from({ length: 12 }, (_, i) => {
    const n = i + 1, brutas = v(n, 'ventas_brutas'), netas = v(n, 'ventas_netas');
    return { pedidos: v(n, 'pedidos'), prendas: v(n, 'prendas'), brutas, descuentos: brutas - netas, netas, costo: v(n, 'costo_ventas'), flete: 0,
      transporte: v(n, 'gasto_logistico'), operacion: v(n, 'apoyo_comercial') + v(n, 'gastos_operacion') };
  });
  return { meses: meses.map(derivar), anual: derivar(anual(meses)), historico: true };
}

// Envíos del año: lo cobrado a los clientes contra lo pagado a cada transportadora, solo en los pedidos enviados que ya
// tienen su costo registrado (los demás se cuentan aparte como "sin costo").
export interface ResumenEnvio { clave: string; despachos: number; conCosto: number; cobrado: number; costo: number }
export interface EnviosAnio { filas: ResumenEnvio[]; total: ResumenEnvio; sinCosto: number; estandar: ResumenEnvio | null }
export function resumenEnvios(pedidos: Pedido[], anio: number): EnviosAnio {
  const vacia = (clave: string): ResumenEnvio => ({ clave, despachos: 0, conCosto: 0, cobrado: 0, costo: 0 });
  const por = new Map<string, ResumenEnvio>(), total = vacia('Total'), estandar = vacia('Tarifa estándar');
  const sumar = (x: ResumenEnvio, p: Pedido, cobrado: number) => { x.despachos++; if (p.costo_envio != null) { x.conCosto++; x.cobrado += cobrado; x.costo += +p.costo_envio; } };
  for (const p of pedidos) {
    if (p.estado !== 'enviado' || +p.fecha.slice(0, 4) !== anio) continue;
    const t = totalesPedido(p), cobrado = t.flete * t.tasa, clave = p.transportadora || 'Sin transportadora';
    if (!por.has(clave)) por.set(clave, vacia(clave));
    sumar(por.get(clave)!, p, cobrado); sumar(total, p, cobrado);
    if (p.envio === 'estandar') sumar(estandar, p, cobrado);
  }
  return { filas: [...por.values()].sort((a, b) => b.despachos - a.despachos || a.clave.localeCompare(b.clave, 'es')), total, sinCosto: total.despachos - total.conCosto, estandar: estandar.conCosto ? estandar : null };
}
